import { assign, createMachine } from "xstate";
import { fetchOrchestrators } from "@nju/data-access-portal-orchestrator";
import { fetchActionToken } from "@nju/data-access-sales-user";
import { isError } from "@nju/result";
import { generalErrorMessage, isValidEmail } from "@nju/utils";

interface IContext {
  email: string;
  formError?: string;
}

type IEvent =
  | {
      type: "SET_INPUT_VALUE";
      name: "email";
      value: string;
    }
  | { type: "SUBMIT" }
  | { type: "GENERAL_ERROR" }
  | { type: "CLIENT_EXIST" }
  | { type: "CLIENT_LOCKED_BY_OTHER_AGENT" }
  | { type: "CLIENT_DOES_NOT_EXIST" }
  | { type: "GO_TO_CLIENT_STATUS" };

export const checkClientPanelMachine = createMachine<IContext, IEvent>(
  {
    context: {
      email: "",
    },
    schema: {
      context: {} as IContext,
      events: {} as IEvent,
    },
    initial: "ready",
    states: {
      ready: {
        on: {
          SET_INPUT_VALUE: {
            actions: "setInputValue",
          },
          SUBMIT: {
            target: "validate",
          },
        },
      },
      validate: {
        entry: "validateForm",
        always: [
          {
            cond: "hasNoInputErrors",
            target: "checkIfClientExist",
          },
          {
            target: "ready",
          },
        ],
      },
      checkIfClientExist: {
        invoke: {
          src: "checkIfClientExist",
        },
        on: {
          GENERAL_ERROR: {
            actions: "setGeneralErrorMessage",
            target: "ready",
          },
          CLIENT_DOES_NOT_EXIST: {
            actions: "setClientDoesNotExistError",
            target: "ready",
          },
          CLIENT_EXIST: {
            target: "checkIfClientIsLockedByOtherAgent",
          },
        },
      },
      checkIfClientIsLockedByOtherAgent: {
        invoke: {
          src: "checkIfClientIsLockedByOtherAgent",
        },
        on: {
          GENERAL_ERROR: {
            actions: "setGeneralErrorMessage",
            target: "ready",
          },
          CLIENT_LOCKED_BY_OTHER_AGENT: {
            actions: "redirectToClientInProgress",
          },
          GO_TO_CLIENT_STATUS: {
            actions: "redirectToClientStatus",
          },
        },
      },
    },
  },
  {
    actions: {
      setInputValue: assign<IContext, IEvent>((context, event) => {
        if (event.type === "SET_INPUT_VALUE") {
          return {
            [event.name]: event.value,
            formError: undefined,
          };
        }

        return context;
      }),
      validateForm: assign<IContext, IEvent>((context) => {
        let formError: string | undefined;

        if (!context.email.trim()) {
          formError = "Wpisz adres e-mail.";
        } else if (!isValidEmail(context.email)) {
          formError = "Wpisz poprawny adres e-mail.";
        }

        return { formError };
      }),
      setClientDoesNotExistError: assign<IContext, IEvent>((context, event) => {
        if (event.type === "CLIENT_DOES_NOT_EXIST") {
          return {
            formError: "Klient o tym adresie e-mail nie istnieje w bazie.",
          };
        }
        return context;
      }),
      setGeneralErrorMessage: assign<IContext, IEvent>((context, event) => {
        if (event.type === "GENERAL_ERROR") {
          return {
            formError: generalErrorMessage,
          };
        }
        return context;
      }),
    },
    guards: {
      hasNoInputErrors: ({ formError }) => {
        return formError === undefined;
      },
    },
    services: {
      checkIfClientExist: ({ email }) => {
        return async (callback) => {
          const checkClientInKeycloak = await fetchActionToken({ email });

          if (isError(checkClientInKeycloak)) {
            if (checkClientInKeycloak.err.code === "CLIENT_NOT_FOUND") {
              return callback({ type: "CLIENT_DOES_NOT_EXIST" });
            }

            return callback({ type: "GENERAL_ERROR" });
          }
          return callback({ type: "CLIENT_EXIST" });
        };
      },
      checkIfClientIsLockedByOtherAgent: () => {
        return async (callback) => {
          const result = await fetchOrchestrators();

          if (isError(result)) {
            if (result.err.code === "CLIENT_LOCKED_BY_OTHER_AGENT") {
              return callback({ type: "CLIENT_LOCKED_BY_OTHER_AGENT" });
            }

            return callback({ type: "GENERAL_ERROR" });
          }

          return callback({ type: "GO_TO_CLIENT_STATUS" });
        };
      },
    },
  },
);
